import React, { useState, useRef } from 'react';
import ScannerTab from '../components/ScannerTab';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Search, PlayCircle, Sparkles
} from 'lucide-react';

const countries = [
  { code: 'MCO', label: 'Colombia', flag: '🇨🇴' },
  { code: 'MLA', label: 'Argentina', flag: '🇦🇷' },
  { code: 'MLM', label: 'México', flag: '🇲🇽' },
  { code: 'MLC', label: 'Chile', flag: '🇨🇱' },
  { code: 'MPE', label: 'Perú', flag: '🇵🇪' },
  { code: 'MLB', label: 'Brasil', flag: '🇧🇷' },
  { code: 'MLU', label: 'Uruguay', flag: '🇺🇾' },
];

export default function SearchProductsPage() {
  const [country, setCountry] = useState('MLM');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [showCountryMenu, setShowCountryMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('demand');
  const [mainTab, setMainTab] = useState('search');
  const [lastQuery, setLastQuery] = useState('');
  const [related, setRelated] = useState([]);
  const requestId = useRef(0);

  const selectedCountry = countries.find(c => c.code === country);

  const handleSearch = async (override) => {
    const text = (typeof override === 'string' ? override : query).trim();
    if (!text) return toast.error('Ingresa un producto o nicho');
    if (typeof override === 'string') setQuery(text);
    const currentRequest = ++requestId.current;
    setSearching(true);
    setRelated([]);
    try {
      const res = await api.post('/research/analyze', { query: text, site: country });
      if (currentRequest === requestId.current) {
        setResults(res.data); setLastQuery(text);
        const top_titles = (res.data.items || []).map(i => i.title).filter(Boolean).slice(0, 15);
        api.post('/research/related', { query: text, site: country, top_titles })
          .then(r => { if (currentRequest === requestId.current) setRelated(r.data.related || []); })
          .catch(() => {});
      }
      toast.success('Análisis completado');
    } catch (error) {
      if (currentRequest === requestId.current) {
        setResults(null);
        toast.error(error.response?.data?.detail || 'Error al analizar');
      }
    } finally {
      setSearching(false);
    }
  };

  return (
    <DashboardLayout
      title="Búsqueda de Productos"
      subtitle="Analiza nichos en Mercado Libre"
      icon={Search}
      backTo="/dashboard"
      headerRight={
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-3 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800">
            <PlayCircle size={16} />
            Ver tutorial
          </button>
        </div>
      }
    >
      <div className="flex gap-2 mb-4">
        {[['search', 'Búsqueda'], ['scanner', 'Escáner']].map(([id, label]) => (
          <button key={id} data-testid={`main-tab-${id}`} onClick={() => setMainTab(id)} className={`px-4 py-2 rounded-lg text-sm ${mainTab === id ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300'}`}>{label}</button>
        ))}
      </div>
      {mainTab === 'scanner' && <ScannerTab marketResults={results} marketQuery={lastQuery} />}
      {mainTab === 'search' && (<>
      {/* Search Section */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
        <div className="flex items-center gap-3">
          {/* Country Selector */}
          <div className="relative">
            <button
              data-testid="country-selector"
              onClick={() => setShowCountryMenu(!showCountryMenu)}
              className="flex items-center gap-2 px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white hover:bg-slate-700 transition-colors min-w-[160px]"
            >
              <span className="text-lg">{selectedCountry?.flag}</span>
              {selectedCountry?.label}
              <span className="ml-auto text-slate-400">▼</span>
            </button>
            {showCountryMenu && (
              <div className="absolute top-full left-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-10 w-48">
                {countries.map(c => (
                  <button
                    key={c.code}
                    onClick={() => { setCountry(c.code); setShowCountryMenu(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-slate-700 ${
                      country === c.code ? 'text-blue-400' : 'text-white'
                    }`}
                  >
                    <span className="text-lg">{c.flag}</span>
                    {c.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search Input */}
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="research-search"
              type="text"
              placeholder="Buscar producto o nicho (ej: auriculares bluetooth)"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-sm text-white placeholder-slate-500"
            />
          </div>

          {/* Search Button */}
          <button
            data-testid="research-analyze-btn"
            onClick={() => handleSearch()}
            disabled={searching}
            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 transition-all"
          >
            <Search size={16} />
            {searching ? 'Analizando...' : 'Analizar'}
          </button>
        </div>

        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-slate-500">
            Introduce el nombre del producto que quieres investigar, idealmente usando 2-3 palabras. Ejemplo: "reloj digital".
          </p>
          <span className="flex items-center gap-1 text-xs text-slate-400"><Sparkles size={12} className="text-blue-400" />Muestra máxima: 25 publicaciones · vendidos históricos como proxy</span>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="mt-6 bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Resultados del Análisis</h3>
          <p className="text-sm text-slate-400 mb-4">{results.verdict}</p>
          <div className="flex gap-2 border-b border-slate-800 mb-4">
            {[['demand', 'Demanda'], ['market', 'Mercado'], ['strategy', 'Estrategia']].map(([id, label]) => <button key={id} onClick={() => setActiveTab(id)} className={`px-4 py-2 text-sm ${activeTab === id ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-400'}`}>{label}</button>)}
          </div>
          {activeTab === 'demand' && <DemandTab results={results} />}
          {activeTab === 'market' && <MarketTab results={results} />}
          {activeTab === 'strategy' && <StrategyTab results={results} />}
          {related.length > 0 && (
            <div className="mt-6 pt-4 border-t border-slate-800" data-testid="related-searches">
              <h4 className="text-sm text-slate-300 mb-2 flex items-center gap-1"><Sparkles size={14} className="text-blue-400" />Búsquedas relacionadas con IA</h4>
              <div className="flex flex-wrap gap-2">
                {related.map(r => (
                  <button key={r.query} title={r.reason} disabled={searching} onClick={() => handleSearch(r.query)}
                    className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-full text-sm text-white hover:bg-slate-700 disabled:opacity-50 text-left">
                    {r.query}{r.reason && <span className="block text-xs text-slate-400">{r.reason}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </>)}
    </DashboardLayout>
  );
}

const fmtNum = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toLocaleString('es-MX', { maximumFractionDigits: 2 }));
const fmtPrice = (v, cur) => (v == null ? '—' : `$${fmtNum(v)}${cur ? ` ${cur}` : ''}`);
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(0)}%`);
const triState = (v) => (v == null ? '—' : v ? 'Sí' : 'No');

function DemandTab({ results }) {
  const demand = results.demand || {};
  const cov = results.sold_quantity_coverage || {};
  const sig = demand.highlight_signal || {};
  const missing = cov.total > 0 && cov.known < cov.total;
  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Base de demanda" value={results.demand_basis ?? '—'} />
        <Metric label="Vendidos (histórico)" value={results.total_sold ?? '—'} />
        <Metric label="Ventas disponibles" value={cov.total != null ? `${cov.known ?? 0} de ${cov.total}` : '—'} />
        <Metric label="Muestra" value={`${results.analyzed_count ?? '—'}/${results.results_count ?? '—'}`} />
      </div>
      {missing && (
        <p className="text-slate-400">
          Ventas disponibles en {cov.known ?? 0} de {cov.total} publicaciones; no se suma un total parcial.
          Las visitas y ventas de competidores no están disponibles vía API, por lo que no se muestran estimaciones.
        </p>
      )}
      <div className="bg-slate-800/60 rounded-lg p-3">
        <p className="text-xs text-slate-500">Señal alterna: más vendidos de la categoría (referencia, no es una cifra de ventas)</p>
        {sig.position != null ? (
          <p className="text-white mt-1">
            Mejor posición #{sig.position} de {sig.highlight_count ?? '—'} · {sig.matched_offer_count ?? 0} publicaciones de la muestra aparecen en el listado
          </p>
        ) : (
          <p className="text-slate-400 mt-1">
            Ninguna publicación de la muestra aparece en los más vendidos de la categoría
            {sig.highlight_count ? ` (${sig.highlight_count} revisados)` : ''}.
          </p>
        )}
      </div>
    </div>
  );
}

function MarketTab({ results }) {
  const st = results.strategy || {};
  const range = st.price_range || results.price_range || {};
  const median = st.median_price ?? results.median_price;
  const cur = results.currency_id;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <Metric label="Precio mín." value={fmtPrice(range.min, cur)} />
        <Metric label="Precio mediano" value={fmtPrice(median, cur)} />
        <Metric label="Precio máx." value={fmtPrice(range.max, cur)} />
        <Metric label="Vendedores distintos" value={st.distinct_seller_count ?? '—'} />
        <Metric label="Concentración top 3" value={fmtPct(st.top3_concentration_pct)} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-300">
          <thead><tr className="text-left text-slate-500"><th className="p-2">Publicación</th><th className="p-2">Precio</th><th className="p-2">Vendidos</th><th className="p-2">Envío FULL</th><th className="p-2">Envío gratis</th></tr></thead>
          <tbody>
            {(results.items || []).map(item => (
              <tr key={item.item_id} className="border-t border-slate-800">
                <td className="p-2"><a className="text-blue-400" href={item.permalink} target="_blank" rel="noreferrer">{item.title}</a></td>
                <td className="p-2">{fmtPrice(item.price, item.currency_id)}</td>
                <td className="p-2">{item.sold_quantity ?? '—'}</td>
                <td className="p-2">{triState(item.is_full)}</td>
                <td className="p-2">{triState(item.free_shipping ?? item.shipping?.free_shipping)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StrategyTab({ results }) {
  const st = results.strategy;
  if (!st) return <p className="text-sm text-slate-400">Sin datos de estrategia para esta búsqueda.</p>;
  const cur = results.currency_id;
  const bands = st.price_bands || [];
  const pres = st.presentations || [];
  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Rango de precio" value={st.price_range?.min != null ? `${fmtPrice(st.price_range.min)} – ${fmtPrice(st.price_range.max)}` : '—'} />
        <Metric label="Precio mediano" value={fmtPrice(st.median_price, cur)} />
        <Metric label="Envío gratis (sobre conocidos)" value={fmtPct(st.free_shipping_pct)} />
        <Metric label="Full (sobre conocidos)" value={fmtPct(st.full_pct)} />
      </div>
      <div className="bg-slate-800/60 rounded-lg p-3">
        <p className="text-xs text-slate-500">Precio de entrada</p>
        <p className="text-white mt-1">{fmtPrice(st.suggested_entry_price, cur)} <span className="text-xs text-blue-400 ml-1">sugerencia</span></p>
        {st.suggested_entry_price_criterion && <p className="text-slate-400 mt-1">{st.suggested_entry_price_criterion}</p>}
      </div>
      {bands.length > 0 && (
        <div>
          <h4 className="text-slate-300 mb-2">Bandas de precio</h4>
          <table className="w-full text-slate-300"><thead><tr className="text-left text-slate-500"><th className="p-2">Banda</th><th className="p-2">Mín.</th><th className="p-2">Mediana</th><th className="p-2">Máx.</th><th className="p-2">Publicaciones</th></tr></thead>
            <tbody>{bands.map(b => <tr key={b.label} className="border-t border-slate-800"><td className="p-2 capitalize">{b.label}</td><td className="p-2">{fmtPrice(b.min)}</td><td className="p-2">{fmtPrice(b.median)}</td><td className="p-2">{fmtPrice(b.max)}</td><td className="p-2">{b.count ?? '—'}</td></tr>)}</tbody></table>
        </div>
      )}
      {pres.length > 0 && (
        <div>
          <h4 className="text-slate-300 mb-2">Presentaciones detectadas</h4>
          <table className="w-full text-slate-300"><thead><tr className="text-left text-slate-500"><th className="p-2">Presentación</th><th className="p-2">Precio mediano</th><th className="p-2">Publicaciones</th></tr></thead>
            <tbody>{pres.map(p => <tr key={`${p.type}-${p.value}`} className="border-t border-slate-800"><td className="p-2">{p.value}</td><td className="p-2">{fmtPrice(p.median_price)}</td><td className="p-2">{p.count ?? '—'}</td></tr>)}</tbody></table>
        </div>
      )}
      <p className="text-xs text-slate-500">Sin estimaciones mensuales: no hay visitas verificadas de competidores.</p>
    </div>
  );
}

function Metric({ label, value }) {
  return <div className="bg-slate-800/60 rounded-lg p-3"><p className="text-xs text-slate-500">{label}</p><p className="text-white mt-1">{value}</p></div>;
}
